import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Coins } from 'lucide-react';
import { fetchBalance, type CreditBalance } from '@/lib/billing';
import { BuyCreditsModal } from '@/components/BuyCreditsModal';

const POLL_INTERVAL_MS = 30_000;

/**
 * Story 50.7 — credit-balance widget shown in the AppShell header.
 *
 * Polls /api/v1/me/credits/balance every 30s and displays the current
 * balance with a low-balance indicator when balance < threshold. Click
 * opens the BuyCreditsModal to start a top-up.
 *
 * Hides itself if the balance endpoint returns 401/403 (e.g. user is
 * unauthenticated or the tenant has billing disabled).
 */
export function CreditBalanceWidget() {
  const { t } = useTranslation();
  const [balance, setBalance] = useState<CreditBalance | null>(null);
  const [hidden, setHidden] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const data = await fetchBalance();
        if (!cancelled) setBalance(data);
      } catch (err: unknown) {
        const status =
          err && typeof err === 'object' && 'response' in err
            ? (err as { response?: { status?: number } }).response?.status
            : undefined;
        if (status === 401 || status === 403) {
          if (!cancelled) setHidden(true);
        }
      }
    }
    load();
    const id = window.setInterval(load, POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, []);

  if (hidden) return null;
  if (balance === null) return null;

  const isLow = balance.balance < balance.lowBalanceThreshold;

  return (
    <>
      <button
        type="button"
        onClick={() => setModalOpen(true)}
        className={`inline-flex items-center gap-2 rounded-md border px-3 py-1.5 text-sm transition hover:bg-accent ${
          isLow ? 'border-amber-700 bg-amber-950/40 text-amber-200' : 'bg-surface-1'
        }`}
        title={t('billing.widget.title')}
        aria-label={t('billing.widget.aria')}
      >
        <Coins className="h-4 w-4" aria-hidden />
        <span className="font-medium tabular-nums">
          {balance.balance.toLocaleString()}
        </span>
        <span className="hidden text-xs text-muted-foreground sm:inline">
          {t('billing.credits_unit')}
        </span>
        {isLow && (
          <span className="ml-1 hidden rounded bg-amber-900/60 px-1 py-0.5 text-[10px] font-semibold uppercase tracking-wide sm:inline">
            {t('billing.widget.low')}
          </span>
        )}
      </button>
      <BuyCreditsModal open={modalOpen} onClose={() => setModalOpen(false)} />
    </>
  );
}
